'use client';

import { Professional } from '@interface/clinic';
import { Product } from '@interface/product';
import {
  hasExperience,
  hasTraining,
  ProfessionalExperience,
  ProfessionalStats,
  ProfessionalTraining,
} from 'app/(web)/components/common/ProfessionalProfile';
import ProductImagesCarousel from 'app/(web)/tratamientos/[slug]/components/ProductImagesCarousel';
import { Dialog, DialogContent } from 'designSystem/Dialog/Dialog';
import { Title } from 'designSystem/Texts/Texts';
import Image from 'next/image';

function ProfessionalContent({ professional }: { professional: Professional }) {
  return (
    <div>
      <div className="p-6">
        {/* Mobile: vertical stack. Desktop: side-by-side */}
        <div className="flex flex-col md:flex-row md:items-start md:gap-8 mb-8">
          {/* Image — small on mobile, half-width on desktop */}
          <div className="w-28 h-28 md:w-1/2 md:aspect-square md:h-auto relative shrink-0 bg-hg-secondary100 rounded-2xl mx-auto md:mx-0 mb-4 md:mb-0">
            <Image
              alt={professional.name}
              src={professional.urlPhoto}
              fill
              sizes="(max-width: 768px) 112px, (max-width: 1200px) 50vw, 33vw"
              className="object-cover rounded-2xl"
            />
          </div>

          {/* Right column: name + stats + training + experience */}
          <div className="flex flex-col gap-4 text-center md:text-left min-w-0">
            {/* Name / title / collegiate */}
            <div>
              <p className="font-light font-gtUltra text-2xl md:text-3xl">
                {professional.tittleAbbreviation
                  ? `${professional.tittleAbbreviation}.`
                  : ''}{' '}
                {professional.name}
              </p>
              <p className="font-light text-hg-black500 mt-1">
                {professional.title}
              </p>
              {professional.collegiateNumber && (
                <p className="font-light text-hg-black500 text-sm mt-1">
                  Nº de colegiado {professional.collegiateNumber}
                </p>
              )}
              {professional.medicalCollege && (
                <p className="font-light text-hg-black500 text-sm mt-1">
                  {professional.medicalCollege}
                </p>
              )}
            </div>

            <ProfessionalStats professional={professional} />

            {hasTraining(professional) && (
              <div>
                <Title className="text-hg-secondary font-light font-gtUltra text-xl mb-4 text-left">
                  Formación de {professional.name}
                </Title>
                <ProfessionalTraining professional={professional} />
              </div>
            )}

            {hasExperience(professional) && (
              <div>
                <Title className="text-hg-secondary font-light font-gtUltra text-xl mb-4 text-left">
                  Más sobre {professional.name}
                </Title>
                <ProfessionalExperience professional={professional} />
              </div>
            )}
          </div>
        </div>
      </div>

      {(professional.beforeAndAfterImages?.length ?? 0) > 0 && (
        <div className="pb-8">
          <div className="px-6 py-4">
            <Title className="text-hg-secondary font-light font-gtUltra text-xl text-left">
              Resultados
            </Title>
          </div>

          <ProductImagesCarousel
            product={
              {
                beforeAndAfterImages: professional.beforeAndAfterImages,
              } as Product
            }
            visibleSlides={2.25}
            zoomOverImages
          />
        </div>
      )}
    </div>
  );
}

export default function WebProfessionalsModal({
  professional,
  open,
  onOpenChange,
}: {
  professional: Professional | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!professional) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="w-4/5 right-0 bg-white"
        onInteractOutside={() => onOpenChange(false)}
        forceOverlay
      >
        <div className="p-6">
          <Title size="2xl" className="font-gtUltra text-hg-secondary">
            Equipo médico
          </Title>
        </div>

        <ProfessionalContent professional={professional} />
      </DialogContent>
    </Dialog>
  );
}
